/**
 * Event tracking logic for EventFoundry
 * Attaches click listeners and sends events to GA4
 */

import { isEditorActive, isPreviewActive } from './editor-overlay.js';
import { devLog } from './dev-logger.js';
import { getParentOrigin } from './parent-origin.js';
import { getAdapter } from './adapters/registry.js';
import { generateEventParams, extractMappedParams } from './params-generator.js';

// AbortController for managing event listener lifecycle
let abortController = null;

function getSelectorSpecificity(selector) {
  // Lightweight CSS specificity score: IDs > classes/attrs/pseudo-classes > tag names.
  const idCount = (selector.match(/#[\w-]+/g) || []).length;
  const classCount = (selector.match(/\.[\w-]+/g) || []).length;
  const attributeCount = (selector.match(/\[[^\]]+\]/g) || []).length;
  const pseudoClassCount = (selector.match(/:[^:\s()[\].#>+~]+(\([^)]*\))?/g) || []).length;
  const elementCount = (selector.match(/(^|[\s>+~])([a-zA-Z][\w-]*)/g) || []).length;

  return (idCount * 100) + ((classCount + attributeCount + pseudoClassCount) * 10) + elementCount;
}

function sortDefinitionsByPriority(definitions) {
  return [...definitions].sort((a, b) => {
    const selectorA = a.triggerConfig?.selector ?? '';
    const selectorB = b.triggerConfig?.selector ?? '';

    const specificityDelta = getSelectorSpecificity(selectorB) - getSelectorSpecificity(selectorA);
    if (specificityDelta !== 0) {
      return specificityDelta;
    }

    const selectorDelta = selectorA.localeCompare(selectorB);
    if (selectorDelta !== 0) {
      return selectorDelta;
    }

    return a.eventKey.localeCompare(b.eventKey);
  });
}

/**
 * Validate event definition
 * @param {Object} eventDef - Event definition from backend
 * @returns {boolean} - True if valid
 */
function validateEventDefinition(eventDef) {
  const { eventKey, eventLabel, triggerConfig, destinations } = eventDef;

  // Required fields
  if (!eventKey || typeof eventKey !== 'string') {
    console.error('EventFoundry: Invalid event - missing eventKey');
    return false;
  }

  if (!eventLabel || typeof eventLabel !== 'string') {
    console.error(`EventFoundry: Invalid event "${eventKey}" - missing eventLabel`);
    return false;
  }

  // Resolve selector from triggerConfig (preferred) or top-level fallback
  const resolvedSelector = triggerConfig?.selector;
  if (!resolvedSelector || typeof resolvedSelector !== 'string') {
    console.warn(`[EventFoundry] Click trigger missing selector: ${eventKey}`);
    return false;
  }

  if (!destinations || typeof destinations !== 'object') {
    console.warn(`EventFoundry: Event "${eventKey}" has no destinations`);
    // Don't reject - allow events with no destinations (will just not fire)
  }

  // Validate CSS selector
  try {
    document.querySelector(resolvedSelector);
  } catch (error) {
    console.error(`EventFoundry: Invalid selector for event "${eventKey}": ${resolvedSelector}`);
    return false;
  }

  return true;
}

/**
 * Validate event definitions and return validation results
 * @param {Array} definitions - Event definitions from API
 * @returns {Object} Object with validDefs and invalidDefs arrays
 */
export function validateSelectors(definitions) {
  const validDefs = [];
  const invalidDefs = [];

  definitions.forEach(def => {
    if (validateEventDefinition(def)) {
      validDefs.push(def);
    } else {
      invalidDefs.push({
        selector: def.triggerConfig?.selector,
        eventKey: def.eventKey,
        eventLabel: def.eventLabel
      });
    }
  });

  return { validDefs, invalidDefs };
}

/**
 * Fire event to all enabled destinations
 * @param {Object} eventDef - Event definition
 * @param {Object} params - Event parameters
 * @param {boolean} devMode - If true, log events but don't fire
 * @param {Object} initResults - Object mapping destination names to initialization status
 * @returns {string[]} - Array of destination names that received the event
 */
function fireEventToDestinations(eventDef, params, devMode, initResults) {
  const { eventKey, destinations } = eventDef;

  // Track which destinations actually received the event
  const firedDestinations = [];

  // Check if destinations object exists
  if (!destinations || Object.keys(destinations).length === 0) {
    devLog(devMode, 'EventFoundry: No destinations configured for this event');
    return firedDestinations;
  }

  // Iterate over event's destination config
  for (const [destName, destConfig] of Object.entries(destinations)) {
    // Skip if destination not enabled for this event
    if (!destConfig.enabled) {
      continue;
    }

    // Skip if destination failed to initialize
    if (!initResults[destName]) {
      console.warn(`EventFoundry: ${destName} not initialized, skipping event`);
      continue;
    }

    const adapter = getAdapter(destName);
    if (!adapter) {
      continue;
    }

    if (devMode) {
      // Dev mode: Log but don't fire
      console.log(`[DEV] Would fire to ${destName}:`, eventKey, params);
      firedDestinations.push(destName);
    } else {
      // Production: Fire to destination
      try {
        adapter.fireEvent(eventKey, eventDef, params);
        firedDestinations.push(destName);
      } catch (error) {
        console.error(`EventFoundry: Error firing to ${destName}:`, error);
      }
    }
  }

  return firedDestinations;
}

/**
 * Normalize a page path for comparison.
 * Strips query string, hash, and trailing slash (except root '/').
 * Handles both 'pathname+search+hash' and full 'href' strings defensively.
 * @param {string|null} rawPath
 * @returns {string}
 */
function normalizePagePath(rawPath) {
  if (!rawPath) return '';
  // Extract pathname: handle full href (starts with http/https) defensively
  const pathname = rawPath.startsWith('http')
    ? new URL(rawPath).pathname
    : rawPath.split('?')[0].split('#')[0];
  // Strip trailing slash, except for root '/'
  return pathname.length > 1 ? pathname.replace(/\/$/, '') : pathname;
}

/**
 * Initialize event tracking
 * @param {Array} definitions - Event definitions from API
 * @param {boolean} devMode - If true, log events but don't send to destinations
 * @param {Object} initResults - Object mapping destination names to initialization status
 * @returns {boolean} True if tracking was initialized, false if all selectors were invalid
 */
export function initTracking(definitions, devMode = false, initResults = {}) {
  if (!definitions || definitions.length === 0) {
    console.log('EventFoundry: No events defined for this site');
    return true; // Not an error - site simply has no events configured
  }

  // Prevent duplicate listeners on re-initialization
  if (abortController) {
    abortController.abort();
  }

  // Filter out unsupported trigger types before validation — skip with dev warning,
  // do not run validateEventDefinition on them (avoids surfacing false errors in preview/tooling)
  const clickDefs = definitions.filter(def => {
    if (def.triggerType !== 'click') {
      console.warn(`[EventFoundry] Unsupported triggerType, skipping: ${def.eventKey} (${def.triggerType})`);
      return false;
    }
    return true;
  });

  if (clickDefs.length === 0) {
    console.log('EventFoundry: No click events configured; tracker initialized in idle mode');
    return true;
  }

  // Validate all selectors upfront to catch invalid ones before attaching listeners
  const { validDefs, invalidDefs } = validateSelectors(clickDefs);

  if (validDefs.length === 0) {
    // All events are invalid - possible cache corruption
    console.error('🚨 EventFoundry: All event selectors invalid - possible cache corruption');
    console.error('Invalid events:', invalidDefs);
    return false; // Signal failure to trigger cache clear
  }

  // Warn if SOME selectors are invalid
  if (invalidDefs.length > 0) {
    console.warn(`EventFoundry: ${invalidDefs.length} of ${clickDefs.length} selectors are invalid and will be skipped`);
  }

  const prioritizedDefs = sortDefinitionsByPriority(validDefs);

  // Create new AbortController for this initialization
  abortController = new AbortController();

  document.addEventListener('click', (event) => {
    // Prevent firing GA4 events during element selection in edit mode
    if (isEditorActive() && !isPreviewActive()) {
      devLog(devMode, 'EventFoundry: Click ignored (editor mode active, not in preview)');
      return;
    }

    // Find ALL matching definitions for this click (only valid ones)
    const currentPath = normalizePagePath(window.location.pathname);

    const matchingDefs = prioritizedDefs.filter(def => {
      // Resolve selector (triggerConfig.selector preferred, top-level for transition fallback)
      const selector = def.triggerConfig?.selector;
      if (!selector) return false;

      // Selector must match clicked element
      if (event.target.closest(selector) === null) return false;

      // pagePath must exist and match current page
      if (def.pagePath == null) {
        devLog(devMode, `[EventFoundry] Event skipped: missing pagePath on event ${def.eventKey}`);
        return false;
      }

      return normalizePagePath(def.pagePath) === currentPath;
    });

    if (matchingDefs.length === 0) {
      devLog(devMode, 'EventFoundry: Click on', event.target, 'matched 0 events');
      return;
    }

    // Fire only the highest-priority match (selector specificity, then lexical tie-breakers)
    const defToFire = matchingDefs[0];

    // Guards for click trigger — both must exist before generateEventParams is called.
    // Do not reorder these guards and the generateEventParams call.
    if (defToFire.triggerType === 'click' && !defToFire.triggerConfig?.selector) {
      console.warn(`[EventFoundry] Event "${defToFire.eventKey}" missing triggerConfig.selector — skipping.`);
      return;
    }

    // Resolve selector for the winning definition
    const defSelector = defToFire.triggerConfig?.selector;

    // Get the clicked element that matched the selector
    const clickedElement = defSelector ? event.target.closest(defSelector) : null;

    if (defToFire.triggerType === 'click' && !clickedElement) {
      console.warn(`[EventFoundry] Event "${defToFire.eventKey}" has no target element — skipping.`);
      return;
    }

    // Generate base parameters using trigger-type-aware generator.
    // Guards above guarantee triggerConfig.selector and element both exist for click.
    const base_params = generateEventParams(clickedElement, defToFire);
    if (base_params === null) {
      // generateEventParams already warned — unknown triggerType, nothing to fire
      return;
    }

    const mapped = extractMappedParams(defToFire.paramMappings ?? [], defToFire.triggerType);

    // If in preview mode, send EVENT_FIRED postMessage to parent for event log
    if (isPreviewActive()) {
      // Get list of enabled destinations for this event (without firing)
      const enabledDestinations = defToFire.destinations
        ? Object.entries(defToFire.destinations)
          .filter(([_, config]) => config.enabled && initResults[_])
          .map(([name, _]) => name)
        : [];

      try {
        const targetOrigin = getParentOrigin();
        if (targetOrigin) {
          const eventFiredPayload = {
            type: 'EVENT_FIRED',
            schema_version: 2,
            timestamp: Date.now(),
            eventKey: defToFire.eventKey,
            eventLabel: defToFire.eventLabel,
            triggerType: defToFire.triggerType,
            destinations: enabledDestinations,
            base_params,
            mapped
          };
          window.parent.postMessage(eventFiredPayload, targetOrigin);
          console.log('→ EVENT_FIRED sent to parent');
          if (window.location.hostname === 'localhost') {
            console.log('[EventFoundry dev] EVENT_FIRED payload:\n', eventFiredPayload);
          }
        } else {
          console.warn('EventFoundry: Cannot send EVENT_FIRED - parent origin not established');
        }
      } catch (error) {
        console.warn('EventFoundry: Failed to send EVENT_FIRED postMessage', error);
      }
    } else {
      // mapped spreads first so base_params always wins on key collision (defense-in-depth;
      // reserved key validation at save time is the primary guard)
      const mergedParams = { ...mapped, ...base_params };

      // Fire to all enabled destinations (production or dev mode)
      const firedDestinations = fireEventToDestinations(defToFire, mergedParams, devMode, initResults);

      // Log event about to fire in dev mode
      devLog(devMode, `EventFoundry: Event fired to ${firedDestinations.length} destination(s):`, firedDestinations);
    }

    // Warn about multiple matches in development
    if (matchingDefs.length > 1) {
      console.warn('EventFoundry: Multiple events matched, firing highest priority:',
        matchingDefs.map(d => `${d.eventLabel} [${d.triggerConfig?.selector}]`)
      );
    }
  }, { capture: true, signal: abortController.signal });

  return true;
}
